// Subscription history (past orders) for /usage.
//
// Fetches the caller's own org orders via GET /subscriptions/orders
// (BE strictly scopes by current_user.organization_id; the user
// cannot view other tenants' orders here). Click any row to open a
// read-only Descriptions drawer.
//
// Patterned on the admin Payment History detail drawer but trimmed
// — the user-facing serializer omits razorpay_signature and
// admin_override_reason, so those keys are absent from the payload
// and never rendered here.

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Card,
  Descriptions,
  Drawer,
  Skeleton,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { fetchData } from "../../api/apiService";
import { SUBSCRIPTIONS } from "../../api/endpointMap";

const { Text } = Typography;

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtINR(value) {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
}

function statusTag(order) {
  const status = (order?.payment_status || "").toLowerCase();
  if (order?.order_type === "refund") return <Tag color="orange">Refund</Tag>;
  if (order?.order_type === "credit") return <Tag color="purple">Credit</Tag>;
  if (status === "paid" || order?.is_paid) return <Tag color="green">Paid</Tag>;
  if (status === "cancelled") return <Tag>Cancelled</Tag>;
  if (status === "failed" || status === "signature_failed")
    return <Tag color="red">{status === "signature_failed" ? "Signature failed" : "Failed"}</Tag>;
  if (status === "refunded") return <Tag color="orange">Refunded</Tag>;
  if (status === "created" || status === "pending")
    return <Tag color="blue">{status === "pending" ? "Pending" : "Created"}</Tag>;
  return <Tag>{status || "-"}</Tag>;
}

export default function OrderHistoryTable() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const raw = await fetchData(SUBSCRIPTIONS.ORDERS, {
        params: { page, page_size: pageSize },
      });
      const payload = raw?.result || raw?.data || raw || {};
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(Number(payload.total) || 0);
    } catch (e) {
      setLoadError(
        e?.detail || e?.message || "Could not load your order history.",
      );
      setItems([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      width: 175,
      render: (v) => fmtDateTime(v),
    },
    {
      title: "Product",
      dataIndex: "product",
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 130,
      align: "right",
      render: (v) => fmtINR(v),
    },
    {
      title: "Status",
      width: 130,
      render: (_, row) => statusTag(row),
    },
    {
      title: "Gateway",
      dataIndex: "gateway_provider",
      width: 110,
      render: (v) => (v ? <Tag>{v}</Tag> : "-"),
    },
    {
      title: "Razorpay order",
      dataIndex: "razorpay_order_id",
      width: 170,
      ellipsis: true,
      render: (v) =>
        v ? (
          <Tooltip title={v}>
            <Text code>{v.slice(0, 12)}…</Text>
          </Tooltip>
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Card title="Subscription history">
      {loadError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Could not load order history"
          description={loadError}
        />
      )}

      {isLoading && items.length === 0 ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : (
        <Table
          size="middle"
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={items}
          onRow={(row) => ({
            onClick: () => {
              setDetail(row);
              setDetailOpen(true);
            },
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showTotal: (t) => `${t.toLocaleString("en-IN")} orders`,
          }}
          scroll={{ x: 900 }}
        />
      )}

      <Drawer
        title="Order detail"
        width={480}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        destroyOnHidden
      >
        {detail && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Order id">
              <Text code>{detail.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {fmtDateTime(detail.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Product">{detail.product || "-"}</Descriptions.Item>
            <Descriptions.Item label="Amount">{fmtINR(detail.amount)}</Descriptions.Item>
            <Descriptions.Item label="Status">{statusTag(detail)}</Descriptions.Item>
            <Descriptions.Item label="Gateway">
              {detail.gateway_provider || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Transaction id">
              <Text code copyable>
                {detail.transaction_id || "-"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Razorpay order id">
              <Text code copyable={!!detail.razorpay_order_id}>
                {detail.razorpay_order_id || "-"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Razorpay payment id">
              <Text code copyable={!!detail.razorpay_payment_id}>
                {detail.razorpay_payment_id || "-"}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Card>
  );
}
